import { clients } from 'lib/https';
import { ConfigurationSettings } from 'models/configuration-settings';
import { SubscriptionDetails } from 'state/store/subscription/subscriptionStore';
import { BILLING_PROFILE_INCOMPLETE, type PlanSeats } from '../utils/seat-pools';
import { makeSubscription, mapCurrentSubscription } from './plan-subscription-service';
import { getBillingProfile, previewQuantityChange, SubscriptionBlocker, updateQuantity } from './subscription-service';

export interface SeatHolder {
  userId: string;
  seatNumber?: number | null;
}

/** One seat's use of one meter this period. It includes what an earlier holder spent. */
export interface PlaceUsage {
  seatNumber: number;
  /** Who holds the seat now; empty when nobody does. */
  userId: string;
  meterKey: string;
  unitLabel: string;
  quantityScale: number;
  included: number;
  used: number;
  remaining: number;
  overage: number;
  periodEndUtc: string;
}

export interface SubscriptionMembers {
  subscriptionId: string;
  purchased: number;
  held: number;
  available: number;
  /** What a scheduled decrease leaves at renewal, or null when none is scheduled. */
  scheduledPlaces?: number | null;
  scheduledAtUtc?: string | null;
  seats: SeatHolder[];
  usage?: PlaceUsage[];
}

/** A seat the signed-in person holds, and the plan it is on. */
export interface HeldPlace {
  subscriptionId: string;
  planCode: string;
  planName: string;
  status: string;
  seatNumber: number;
  currentPeriodEndUtc: string;
  cancelAtPeriodEnd: boolean;
}

/** One plan the organization bought seats on, with who holds them. Every Klax plan is user-wise. */
export interface SeatPool {
  subscription: SubscriptionDetails;
  members: SubscriptionMembers;
}

interface Envelope<T> {
  success?: boolean;
  data: T | null;
  error?: { code?: string; message?: string } | string | null;
}

interface AssignmentResult {
  assigned: SeatHolder[];
  refused: { userId: string; reasonCode: string; reason: string }[];
}

const subscriptionsUrl = (config: ConfigurationSettings) =>
  `https://${config.paths.baseUrl}${config.paths.businessService.subscriptions}`;

// No message of our own: the caller shows its translated fallback when the server names none.
const unwrap = <T>(res: Envelope<T>): T => {
  if (res?.success === false || res?.data == null) {
    const error = res?.error;
    throw new Error((typeof error === 'string' ? error : error?.message) || '');
  }
  return res.data;
};

/** The seats the signed-in person holds. Empty when they have no plan. */
export const getMyPlaces = async (config: ConfigurationSettings): Promise<HeldPlace[]> => {
  const res = await clients.get<Envelope<HeldPlace[]>>(`${subscriptionsUrl(config)}/mine`, config.blocksKey);
  return res?.data ?? [];
};

/** An error carrying a Utilities code, read the same way as one Utilities sent. */
const codedError = (code: string, message: string) =>
  Object.assign(new Error(message), { error: { error: { code, message } } });

/** The organization's seat-based subscriptions. A cancelled one is no longer listed. */
export const getMemberSubscriptions = async (config: ConfigurationSettings): Promise<SubscriptionDetails[]> => {
  const res = await clients.get<Envelope<unknown[]>>(
    `${subscriptionsUrl(config)}/member-based`,
    config.blocksKey
  );
  return (res?.data ?? [])
    .map((item) => mapCurrentSubscription(item))
    .filter((item): item is SubscriptionDetails => item !== null);
};

export const getSeatPools = async (config: ConfigurationSettings): Promise<SeatPool[]> => {
  const subscriptions = await getMemberSubscriptions(config);

  return Promise.all(
    subscriptions.map(async (subscription) => {
      const members = await clients.get<Envelope<SubscriptionMembers>>(
        `${subscriptionsUrl(config)}/${subscription.subscriptionId}/members`,
        config.blocksKey
      );
      return { subscription, members: unwrap(members) };
    })
  );
};

/** What a seat change costs now and at renewal, and anything that would refuse it. */
export interface SeatQuote {
  dueNowMinor: number;
  nextRenewalMinor: number;
  currencyCode: string;
  blockers: SubscriptionBlocker[];
}

/** Where the bought seats live, and the payment page to open when a first purchase needs one. */
export interface SeatPurchase {
  subscriptionId: string;
  checkoutUrl: string | null;
}

const newSubscriptionBody = (entry: PlanSeats, quantity: number) => ({
  planCode: entry.code,
  priceId: entry.price?.priceId ?? '',
  quantities: [{ itemKey: entry.itemKey, quantity }],
  timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
});

const quantityBody = (entry: PlanSeats, quantity: number) => ({
  version: entry.pool?.subscription.version ?? 0,
  quantities: [{ itemKey: entry.itemKey, quantity }],
});

/** The price of moving a plan to `quantity` seats: a quantity change, or a first subscription. */
export const quoteSeats = async (
  entry: PlanSeats,
  quantity: number,
  config: ConfigurationSettings
): Promise<SeatQuote> => {
  if (entry.pool) {
    const preview = await previewQuantityChange(
      entry.pool.subscription.subscriptionId,
      quantityBody(entry, quantity),
      config
    );
    return {
      dueNowMinor: preview.proratedChargeMinor,
      nextRenewalMinor: preview.nextRenewalAmountMinor,
      currencyCode: preview.currencyCode,
      blockers: preview.blockers ?? [],
    };
  }
  const res = await clients.post<Envelope<{
    totalDueNowMinor: number;
    nextRenewalAmountMinor: number;
    currencyCode: string;
    blockers?: SubscriptionBlocker[];
  }>>(`${subscriptionsUrl(config)}/preview`, config.blocksKey, JSON.stringify(newSubscriptionBody(entry, quantity)));
  const preview = unwrap(res);
  return {
    dueNowMinor: preview.totalDueNowMinor,
    nextRenewalMinor: preview.nextRenewalAmountMinor,
    currencyCode: preview.currencyCode,
    blockers: preview.blockers ?? [],
  };
};

/**
 * Sets a plan to `quantity` seats. A plan already held changes its quantity on the saved card; a
 * plan never bought gets its first subscription, which may hand back a payment page to finish.
 */
export const buySeats = async (
  entry: PlanSeats,
  quantity: number,
  config: ConfigurationSettings
): Promise<SeatPurchase> => {
  if (entry.pool) {
    const subscriptionId = entry.pool.subscription.subscriptionId;
    await updateQuantity(subscriptionId, quantityBody(entry, quantity), config);
    return { subscriptionId, checkoutUrl: null };
  }
  // The first payment goes to a hosted checkout, and the invoice needs these details. Asking for
  // them here, before the checkout opens, keeps the admin from paying and then being refused.
  const profile = await getBillingProfile(config);
  if (!profile?.isComplete) {
    throw codedError(BILLING_PROFILE_INCOMPLETE, '');
  }
  const created = mapCurrentSubscription(
    await makeSubscription(newSubscriptionBody(entry, quantity), config)
  );
  if (!created) {
    throw new Error('');
  }
  return { subscriptionId: created.subscriptionId, checkoutUrl: created.checkoutUrl ?? null };
};

/**
 * Puts people on a plan's seats in one call. Utilities answers per person, so a refusal for anyone
 * is raised with its reason; the people it did seat stay seated.
 */
export const assignSeats = async (
  subscriptionId: string,
  userIds: string[],
  config: ConfigurationSettings
): Promise<void> => {
  const res = await clients.post<Envelope<AssignmentResult>>(
    `${subscriptionsUrl(config)}/${subscriptionId}/members`,
    config.blocksKey,
    JSON.stringify({ userIds })
  );
  const refused = unwrap(res).refused ?? [];
  if (refused.length > 0) {
    throw new Error(refused.map((item) => item.reason || item.reasonCode).join(' '));
  }
};

/**
 * Opens Utilities' hosted page for saving a card against a subscription's billing account. Nothing is
 * charged there; the next seat change charges the saved card. Returns the page to send the admin to.
 */
export const startCardSetup = async (subscriptionId: string, config: ConfigurationSettings): Promise<string> => {
  const res = await clients.post<Envelope<{ pendingCheckout?: { checkoutUrl?: string | null } | null; checkoutUrl?: string | null }>>(
    `${subscriptionsUrl(config)}/${subscriptionId}/payment-method/setup`,
    config.blocksKey,
    JSON.stringify({})
  );
  const data = unwrap(res);
  const url = data.pendingCheckout?.checkoutUrl ?? data.checkoutUrl;
  if (!url) {
    throw new Error('');
  }
  return url;
};

/**
 * Cancels a plan for everyone on it. Immediately: the plan ends now. Otherwise it keeps granting
 * until the period ends, then stops renewing. Either way Utilities releases the seats itself. A plan
 * whose first payment never went through always ends at once.
 */
export const cancelPlan = async (pool: SeatPool, now: boolean, config: ConfigurationSettings): Promise<void> => {
  const { subscriptionId, status } = pool.subscription;
  const immediately = now || status === 'Incomplete';
  const res = await clients.delete<Envelope<unknown>>(
    `${subscriptionsUrl(config)}/${subscriptionId}?immediately=${immediately}`,
    config.blocksKey
  );
  unwrap(res);
};

/** Takes back a cancellation scheduled for the end of the period; the plan renews as before. */
export const keepPlan = async (subscriptionId: string, config: ConfigurationSettings): Promise<void> => {
  const res = await clients.delete<Envelope<unknown>>(
    `${subscriptionsUrl(config)}/${subscriptionId}/cancellation`,
    config.blocksKey
  );
  unwrap(res);
};

export const releaseSeat = async (
  subscriptionId: string,
  userId: string,
  config: ConfigurationSettings
): Promise<void> => {
  const res = await clients.delete<Envelope<SeatHolder>>(
    `${subscriptionsUrl(config)}/${subscriptionId}/members/${userId}`,
    config.blocksKey
  );
  unwrap(res);
};

/**
 * Tells Klax's business service these people were just given seats, so their models, limits
 * and stored work follow within seconds. It answers at once and does the work itself, so a
 * failure here is nothing to show: its ten-minute sweep catches up either way.
 */
export const requestSeatSync = (userIds: string[], config: ConfigurationSettings): void => {
  const base = config.paths.klaxServiceBaseUrl;
  if (!base || userIds.length === 0) return;
  clients
    .post(`${base.replace(/\/$/, '')}${config.paths.seatSync}`, config.blocksKey, JSON.stringify({ user_ids: userIds }))
    .catch(() => undefined);
};

export interface SeatRelease {
  subscriptionId: string;
  userId: string;
}

/**
 * Moves people from their old seats onto a plan's seats. The old seats are freed first: Utilities
 * refuses a seat while the person holds one on a plan that meters the same usage. If seating then
 * fails, the old seats are given back, so nobody is left without a plan.
 */
export const moveOntoSeats = async (
  subscriptionId: string,
  userIds: string[],
  releases: SeatRelease[],
  config: ConfigurationSettings
): Promise<void> => {
  for (const release of releases) {
    await releaseSeat(release.subscriptionId, release.userId, config);
  }
  try {
    await assignSeats(subscriptionId, userIds, config);
  } catch (error) {
    for (const release of releases) {
      await assignSeats(release.subscriptionId, [release.userId], config).catch(() => undefined);
    }
    throw error;
  }
};

/** One invoice, trial invoice or credit note from Utilities' document ledger. Only the fields shown are typed. */
export interface SubscriptionInvoice {
  documentId: string;
  documentNumber: string;
  documentType: string;
  /** Sent as the enum's name, such as "Paid". */
  status: string;
  issuedAtUtc: string;
  currencyCode: string;
  planName: string;
  periodStartUtc?: string | null;
  /** Minor units; int64 may come back as a string. */
  amounts: { totalMinor: number | string };
  lines?: { description: string; quantity?: number | string | null; itemKey?: string | null }[];
}

/** The organization's subscription invoices, newest first. One page of up to 100. */
export const getSubscriptionInvoices = async (config: ConfigurationSettings): Promise<SubscriptionInvoice[]> => {
  const res = await clients.get<Envelope<{ items: SubscriptionInvoice[] }>>(
    `${subscriptionsUrl(config)}/invoices?PageSize=100`,
    config.blocksKey
  );
  return unwrap(res).items ?? [];
};
